import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Application from "../../Application";
import { IRegisteredApplicant } from "../../model/applicant/RegisteredApplicant";
import { TApplicantsAppointmentPresentationModel } from "./model/ApplicantsAppointmentPresentationModel";
import { RoleConstants } from "../../common/Constants";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import RegisterApplicantInteractor from "../../interactor/applicants/RegisterApplicantInteractor";
import { IParty } from "../../model/applicant/Party";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import GetRegisteredApplicants from "../../interactor/applicants/GetRegisteredApplicantsInteractor";

export interface TRegisteredApplicantsPresenter extends TLoadingAwarePresenter {
  registeredApplicants: IRegisteredApplicant[];
  applicantsAppointments?: TApplicantsAppointmentPresentationModel[];
  parties: IParty[];
  unregisteredParties: IParty[];
}

export interface IRegisteredApplicantsPresenter
  extends TRegisteredApplicantsPresenter, TPresentable {
  loadInitialData(
    registeredApplicants: IRegisteredApplicant[],
    parties: IParty[]
  ): void;
  showApplicantDetails(applicantId: number): void;
  canShowApplicantDetails(): boolean;
  canRegisterApplicant(): boolean;
  registerApplicant(partyId: string): Promise<void>;
}

const defaultState: TRegisteredApplicantsPresenter = {
  registeredApplicants: [],
  applicantsAppointments: [],
  parties: [],
  unregisteredParties: []
};

const RegisteredApplicantsPresenter = withStore<
  IRegisteredApplicantsPresenter,
  TRegisteredApplicantsPresenter
>(
  ({
    application,
    store,
    loader,
    translate
  }): IRegisteredApplicantsPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TRegisteredApplicantsPresenter>();

    loader.start("registeredApplicantsLoader");

    const loadInitialData = (
      registeredApplicants: IRegisteredApplicant[],
      parties: IParty[]
    ) => {
      const registeredApplicantsPartyIds = registeredApplicants.map(
        (registeredApplicant: IRegisteredApplicant) =>
          registeredApplicant.partyId
      );
      const unregisteredParties = parties.filter(
        (party: IParty) => !registeredApplicantsPartyIds.includes(party.id)
      );
      _store.update({
        registeredApplicants,
        parties,
        unregisteredParties
      });
      loader.stop("registeredApplicantsLoader");
    };

    const showApplicantDetails = (applicantId: number) => {
      _application.navigator.push({
        pathname: `/registered-applicants/${applicantId}/details`
      });
    };

    const canShowApplicantDetails = () => {
      const state: any = _store.getState();
      return (
        state.roles.includes(RoleConstants.MIK_BO.MIK_OSOBLJE) ||
        state.roles.includes(RoleConstants.MIK_BO.MIK_OSOBLJE_PDNS)
      );
    };

    const canRegisterApplicant = () => {
      const state: any = _store.getState();
      return state.roles.includes(RoleConstants.MIK_BO.MIK_OSOBLJE);
    };

    const refreshRegisteredApplicants = async () => {
      try {
        loader.start("registeredApplicantsLoader");
        const registeredApplicants = await _application.container
          .resolve<GetRegisteredApplicants>("getRegisteredApplicants")
          .execute();
        _store.update({ registeredApplicants });
        loader.stop("registeredApplicantsLoader");
      } catch (error) {
        loader.stop("registeredApplicantsLoader");

        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    const registerApplicant = async (partyId: string) => {
      try {
        await _application.container
          .resolve<RegisterApplicantInteractor>("registerApplicant")
          .execute(partyId);
        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(
            translate("registered_applicant_page.messages.create_success")
          );
        await refreshRegisteredApplicants();
      } catch (error) {
        _application.container
          .resolve<ShowErrorMessageInteractor>("showErrorMessage")
          .execute((error as any).message);
      }
    };

    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      loadInitialData,
      showApplicantDetails,
      canShowApplicantDetails,
      canRegisterApplicant,
      registerApplicant
    };
  },
  defaultState
);

export default RegisteredApplicantsPresenter;
